import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  IconButton,
  Chip,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Switch,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Timer as TimerIcon,
  Receipt as InvoiceIcon,
  AccessTime as ClockIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { format, parseISO, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import {
  useTimeEntries,
  useTimeEntryStats,
  useRunningTimer,
  useTimeEntryMutations,
} from '../../hooks/useTimeEntries';
import { useClients } from '../../hooks/useClients';
import { useProjects } from '../../hooks/useProjects';
import type { TimeEntryFilters, TimeEntryPopulated } from '../../types/timeEntry';
import ConfirmDialog from '../../components/common/ConfirmDialog';

// Format duration from minutes to HH:MM:SS
const formatDuration = (minutes: number): string => {
  const hrs = Math.floor(minutes / 60);
  const mins = Math.floor(minutes % 60);
  const secs = Math.floor((minutes * 60) % 60);
  return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Format money from cents
const formatMoney = (cents: number): string => {
  return `$${(cents / 100).toFixed(2)}`;
};

// Helper to get clientId from project (handles both string and object)
const getProjectClientId = (project: { clientId?: string | { _id: string } }): string | undefined => {
  if (!project.clientId) return undefined;
  if (typeof project.clientId === 'string') return project.clientId;
  return project.clientId._id;
};

// Live timer display component
const LiveTimer: React.FC<{ startTime: string; pausedAt?: string; breaks?: { duration?: number }[] }> = ({
  startTime,
  pausedAt,
  breaks = [],
}) => {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const start = new Date(startTime).getTime();
    const totalBreakMinutes = breaks.reduce((sum, b) => sum + (b.duration || 0), 0);

    const updateElapsed = () => {
      const now = pausedAt ? new Date(pausedAt).getTime() : Date.now();
      const totalMinutes = (now - start) / 60000;
      setElapsed(Math.max(0, totalMinutes - totalBreakMinutes));
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [startTime, pausedAt, breaks]);

  return (
    <Typography variant="h3" component="span" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
      {formatDuration(elapsed)}
    </Typography>
  );
};

export const TimeTrackingPage: React.FC = () => {
  // Filters state
  const [filters, setFilters] = useState<TimeEntryFilters>({
    page: 1,
    limit: 20,
    sortBy: 'startTime',
    sortOrder: 'desc',
  });

  // Date range presets
  const [dateRange, setDateRange] = useState<'today' | 'week' | 'month' | 'custom'>('week');
  const [customStartDate, setCustomStartDate] = useState<Date | null>(null);
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null);

  // Timer form state
  const [timerClientId, setTimerClientId] = useState('');
  const [timerProjectId, setTimerProjectId] = useState('');
  const [timerDescription, setTimerDescription] = useState('');
  const [timerBillable, setTimerBillable] = useState(true);

  // Manual entry dialog
  const [manualEntryOpen, setManualEntryOpen] = useState(false);
  const [manualEntry, setManualEntry] = useState<{
    clientId: string;
    projectId: string;
    description: string;
    date: Date | null;
    startTime: Date | null;
    endTime: Date | null;
    billable: boolean;
    hourlyRate: number;
  }>({
    clientId: '',
    projectId: '',
    description: '',
    date: new Date(),
    startTime: null,
    endTime: null,
    billable: true,
    hourlyRate: 0,
  });

  // Edit entry dialog (TODO: implement edit functionality)
  const [_editEntryOpen, setEditEntryOpen] = useState(false);
  const [_editingEntry, setEditingEntry] = useState<TimeEntryPopulated | null>(null);

  // Delete confirmation
  const [deleteEntry, setDeleteEntry] = useState<TimeEntryPopulated | null>(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // Compute date range for filters
  useEffect(() => {
    const now = new Date();
    let start: string | undefined;
    let end: string | undefined;

    switch (dateRange) {
      case 'today':
        start = format(now, 'yyyy-MM-dd') + 'T00:00:00.000Z';
        end = format(now, 'yyyy-MM-dd') + 'T23:59:59.999Z';
        break;
      case 'week':
        start = startOfWeek(now, { weekStartsOn: 1 }).toISOString();
        end = endOfWeek(now, { weekStartsOn: 1 }).toISOString();
        break;
      case 'month':
        start = startOfMonth(now).toISOString();
        end = endOfMonth(now).toISOString();
        break;
      case 'custom':
        if (customStartDate) start = customStartDate.toISOString();
        if (customEndDate) end = customEndDate.toISOString();
        break;
    }

    setFilters((prev) => ({ ...prev, startDate: start, endDate: end, page: 1 }));
  }, [dateRange, customStartDate, customEndDate]);

  // Data fetching
  const { data: entriesData, isLoading: entriesLoading } = useTimeEntries(filters);
  const { data: statsData } = useTimeEntryStats({
    startDate: filters.startDate,
    endDate: filters.endDate,
    clientId: filters.clientId,
  });
  const { data: runningData } = useRunningTimer();
  const { data: clientsData } = useClients();
  const { data: projectsData } = useProjects();

  const mutations = useTimeEntryMutations();

  // Extract data
  const entries = entriesData?.data?.entries || [];
  const pagination = entriesData?.data?.pagination;
  const stats = statsData?.data;
  const runningEntry = runningData?.data?.entry;
  const clients = clientsData?.clients || [];
  const projects = projectsData?.data?.projects || [];

  // Get client hourly rate
  const getClientRate = (clientId: string): number => {
    const client = clients.find((c) => c._id === clientId);
    return client?.defaultHourlyRate || 0;
  };

  // Handle start timer
  const handleStartTimer = async () => {
    if (!timerClientId || !timerDescription.trim()) {
      setSnackbar({ open: true, message: 'Please select a client and enter a description', severity: 'error' });
      return;
    }

    try {
      await mutations.createTimeEntry.mutateAsync({
        clientId: timerClientId,
        projectId: timerProjectId || undefined,
        description: timerDescription.trim(),
        billable: timerBillable,
        hourlyRate: getClientRate(timerClientId),
      });
      setSnackbar({ open: true, message: 'Timer started', severity: 'success' });
      // Clear form
      setTimerDescription('');
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error?.message || 'Failed to start timer',
        severity: 'error',
      });
    }
  };

  // Handle stop timer
  const handleStopTimer = async () => {
    if (!runningEntry) return;
    try {
      await mutations.stopTimer.mutateAsync(runningEntry._id);
      setSnackbar({ open: true, message: 'Timer stopped', severity: 'success' });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error?.message || 'Failed to stop timer',
        severity: 'error',
      });
    }
  };

  // Handle pause/resume timer
  const handlePauseResume = async () => {
    if (!runningEntry) return;
    try {
      if (runningEntry.status === 'running') {
        await mutations.pauseTimer.mutateAsync(runningEntry._id);
        setSnackbar({ open: true, message: 'Timer paused', severity: 'success' });
      } else {
        await mutations.resumeTimer.mutateAsync(runningEntry._id);
        setSnackbar({ open: true, message: 'Timer resumed', severity: 'success' });
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error?.message || 'Failed to update timer',
        severity: 'error',
      });
    }
  };

  // Handle manual entry save
  const handleSaveManualEntry = async () => {
    if (!manualEntry.clientId || !manualEntry.description.trim() || !manualEntry.date || !manualEntry.startTime || !manualEntry.endTime) {
      setSnackbar({ open: true, message: 'Please fill in all required fields', severity: 'error' });
      return;
    }

    // Combine date with time
    const startDateTime = new Date(manualEntry.date);
    startDateTime.setHours(manualEntry.startTime.getHours(), manualEntry.startTime.getMinutes(), 0, 0);

    const endDateTime = new Date(manualEntry.date);
    endDateTime.setHours(manualEntry.endTime.getHours(), manualEntry.endTime.getMinutes(), 0, 0);

    if (endDateTime <= startDateTime) {
      setSnackbar({ open: true, message: 'End time must be after start time', severity: 'error' });
      return;
    }

    try {
      await mutations.createTimeEntry.mutateAsync({
        clientId: manualEntry.clientId,
        projectId: manualEntry.projectId || undefined,
        description: manualEntry.description.trim(),
        billable: manualEntry.billable,
        hourlyRate: manualEntry.hourlyRate || getClientRate(manualEntry.clientId),
        startTime: startDateTime.toISOString(),
        endTime: endDateTime.toISOString(),
      });
      setSnackbar({ open: true, message: 'Time entry created', severity: 'success' });
      setManualEntryOpen(false);
      setManualEntry({
        clientId: '',
        projectId: '',
        description: '',
        date: new Date(),
        startTime: null,
        endTime: null,
        billable: true,
        hourlyRate: 0,
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error?.message || 'Failed to create entry',
        severity: 'error',
      });
    }
  };

  // Handle delete
  const handleDeleteEntry = async () => {
    if (!deleteEntry) return;
    try {
      await mutations.deleteTimeEntry.mutateAsync(deleteEntry._id);
      setSnackbar({ open: true, message: 'Time entry deleted', severity: 'success' });
      setDeleteEntry(null);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error?.message || 'Failed to delete entry',
        severity: 'error',
      });
    }
  };

  // Filter projects by selected client
  const filteredProjects = useMemo(() => {
    if (!timerClientId) return projects;
    return projects.filter((p) => getProjectClientId(p) === timerClientId);
  }, [projects, timerClientId]);

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
        {/* Page Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" fontWeight="bold">
            Time Tracking
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setManualEntryOpen(true)}
          >
            Manual Entry
          </Button>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ClockIcon sx={{ color: 'primary.main', mr: 1 }} />
                  <Typography color="text.secondary" variant="body2">
                    Total Hours
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight="bold">
                  {stats?.totalHours?.toFixed(1) || '0'}h
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <MoneyIcon sx={{ color: 'success.main', mr: 1 }} />
                  <Typography color="text.secondary" variant="body2">
                    Billable Hours
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight="bold">
                  {stats?.billableHours?.toFixed(1) || '0'}h
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TrendingIcon sx={{ color: 'warning.main', mr: 1 }} />
                  <Typography color="text.secondary" variant="body2">
                    Uninvoiced
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight="bold">
                  {stats?.uninvoicedHours?.toFixed(1) || '0'}h
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <InvoiceIcon sx={{ color: 'info.main', mr: 1 }} />
                  <Typography color="text.secondary" variant="body2">
                    Billable Amount
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight="bold">
                  {formatMoney(stats?.totalBillableAmount || 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Timer Section */}
        <Paper sx={{ p: 3, mb: 3 }}>
          {runningEntry ? (
            // Running Timer Display
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="overline" color="text.secondary">
                Currently Tracking
              </Typography>
              <Box sx={{ my: 2 }}>
                <LiveTimer
                  startTime={runningEntry.startTime}
                  pausedAt={runningEntry.pausedAt}
                  breaks={runningEntry.breaks}
                />
              </Box>
              <Typography variant="h6" gutterBottom>
                {runningEntry.description}
              </Typography>
              <Typography color="text.secondary" gutterBottom>
                {typeof runningEntry.clientId === 'object'
                  ? `${runningEntry.clientId.firstName} ${runningEntry.clientId.lastName}`
                  : runningEntry.clientName}
                {runningEntry.projectName && ` • ${runningEntry.projectName}`}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                <Chip
                  label={runningEntry.status === 'paused' ? 'Paused' : 'Running'}
                  color={runningEntry.status === 'paused' ? 'warning' : 'success'}
                />
                {runningEntry.billable && <Chip label="Billable" variant="outlined" />}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
                <IconButton
                  onClick={handlePauseResume}
                  sx={{
                    bgcolor: runningEntry.status === 'paused' ? 'success.main' : 'warning.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: runningEntry.status === 'paused' ? 'success.dark' : 'warning.dark',
                    },
                  }}
                  size="large"
                >
                  {runningEntry.status === 'paused' ? <PlayIcon /> : <PauseIcon />}
                </IconButton>
                <IconButton
                  onClick={handleStopTimer}
                  sx={{
                    bgcolor: 'error.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'error.dark' },
                  }}
                  size="large"
                >
                  <StopIcon />
                </IconButton>
              </Box>
            </Box>
          ) : (
            // Start Timer Form
            <Box>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TimerIcon /> Start Timer
              </Typography>
              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Client *</InputLabel>
                    <Select
                      value={timerClientId}
                      label="Client *"
                      onChange={(e) => {
                        setTimerClientId(e.target.value);
                        setTimerProjectId('');
                      }}
                    >
                      {clients.map((client) => (
                        <MenuItem key={client._id} value={client._id}>
                          {client.firstName} {client.lastName}
                          {client.company && ` (${client.company})`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Project</InputLabel>
                    <Select
                      value={timerProjectId}
                      label="Project"
                      onChange={(e) => setTimerProjectId(e.target.value)}
                      disabled={!timerClientId}
                    >
                      <MenuItem value="">None</MenuItem>
                      {filteredProjects.map((project) => (
                        <MenuItem key={project._id} value={project._id}>
                          {project.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="What are you working on? *"
                    value={timerDescription}
                    onChange={(e) => setTimerDescription(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleStartTimer();
                    }}
                  />
                </Grid>
                <Grid item xs={6} md={1}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={timerBillable}
                        onChange={(e) => setTimerBillable(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Billable"
                  />
                </Grid>
                <Grid item xs={6} md={1}>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<PlayIcon />}
                    onClick={handleStartTimer}
                    disabled={mutations.createTimeEntry.isPending}
                    fullWidth
                  >
                    Start
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}
        </Paper>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={dateRange}
                  label="Date Range"
                  onChange={(e) => setDateRange(e.target.value as typeof dateRange)}
                >
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="week">This Week</MenuItem>
                  <MenuItem value="month">This Month</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {dateRange === 'custom' && (
              <>
                <Grid item xs={6} sm={3} md={2}>
                  <DatePicker
                    label="Start Date"
                    value={customStartDate}
                    onChange={setCustomStartDate}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                  <DatePicker
                    label="End Date"
                    value={customEndDate}
                    onChange={setCustomEndDate}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Client</InputLabel>
                <Select
                  value={filters.clientId || ''}
                  label="Client"
                  onChange={(e) => setFilters((prev) => ({ ...prev, clientId: e.target.value || undefined, page: 1 }))}
                >
                  <MenuItem value="">All Clients</MenuItem>
                  {clients.map((client) => (
                    <MenuItem key={client._id} value={client._id}>
                      {client.firstName} {client.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status || 'all'}
                  label="Status"
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      status: e.target.value as TimeEntryFilters['status'],
                      page: 1,
                    }))
                  }
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="running">Running</MenuItem>
                  <MenuItem value="paused">Paused</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="invoiced">Invoiced</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Billable</InputLabel>
                <Select
                  value={filters.billable || 'all'}
                  label="Billable"
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      billable: e.target.value as TimeEntryFilters['billable'],
                      page: 1,
                    }))
                  }
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="true">Billable</MenuItem>
                  <MenuItem value="false">Non-Billable</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Time Entries Table */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Project</TableCell>
                  <TableCell align="right">Duration</TableCell>
                  <TableCell align="right">Rate</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entriesLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No time entries found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  entries.map((entry) => {
                    const clientName =
                      typeof entry.clientId === 'object'
                        ? `${entry.clientId.firstName} ${entry.clientId.lastName}`
                        : entry.clientName || 'Unknown';
                    const projectName =
                      typeof entry.projectId === 'object' ? entry.projectId?.name : entry.projectName;
                    const amount = entry.billable
                      ? Math.round((entry.netDuration / 60) * entry.hourlyRate)
                      : 0;

                    return (
                      <TableRow key={entry._id} hover>
                        <TableCell>
                          <Typography variant="body2">
                            {format(parseISO(entry.startTime), 'MMM d, yyyy')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {format(parseISO(entry.startTime), 'h:mm a')}
                            {entry.endTime && ` - ${format(parseISO(entry.endTime), 'h:mm a')}`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {entry.description}
                          </Typography>
                        </TableCell>
                        <TableCell>{clientName}</TableCell>
                        <TableCell>{projectName || '-'}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontFamily="monospace">
                            {formatDuration(entry.netDuration)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {entry.billable ? formatMoney(entry.hourlyRate) + '/hr' : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {entry.billable ? formatMoney(amount) : '-'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={entry.status}
                            size="small"
                            color={
                              entry.status === 'running'
                                ? 'success'
                                : entry.status === 'paused'
                                ? 'warning'
                                : entry.status === 'invoiced'
                                ? 'info'
                                : 'default'
                            }
                          />
                        </TableCell>
                        <TableCell align="right">
                          {entry.status !== 'invoiced' && (
                            <>
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setEditingEntry(entry);
                                    setEditEntryOpen(true);
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => setDeleteEntry(entry)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {pagination && pagination.totalPages > 1 && (
            <TablePagination
              component="div"
              count={pagination.total}
              page={pagination.page - 1}
              onPageChange={(_, page) => setFilters((prev) => ({ ...prev, page: page + 1 }))}
              rowsPerPage={pagination.limit}
              onRowsPerPageChange={(e) =>
                setFilters((prev) => ({ ...prev, limit: parseInt(e.target.value, 10), page: 1 }))
              }
              rowsPerPageOptions={[10, 20, 50]}
            />
          )}
        </Paper>

        {/* Manual Entry Dialog */}
        <Dialog open={manualEntryOpen} onClose={() => setManualEntryOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add Manual Time Entry</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Client *</InputLabel>
                  <Select
                    value={manualEntry.clientId}
                    label="Client *"
                    onChange={(e) => {
                      const clientId = e.target.value;
                      setManualEntry((prev) => ({
                        ...prev,
                        clientId,
                        projectId: '',
                        hourlyRate: getClientRate(clientId),
                      }));
                    }}
                  >
                    {clients.map((client) => (
                      <MenuItem key={client._id} value={client._id}>
                        {client.firstName} {client.lastName}
                        {client.company && ` (${client.company})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Project</InputLabel>
                  <Select
                    value={manualEntry.projectId}
                    label="Project"
                    onChange={(e) => setManualEntry((prev) => ({ ...prev, projectId: e.target.value }))}
                    disabled={!manualEntry.clientId}
                  >
                    <MenuItem value="">None</MenuItem>
                    {projects
                      .filter((p) => getProjectClientId(p) === manualEntry.clientId)
                      .map((project) => (
                        <MenuItem key={project._id} value={project._id}>
                          {project.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Description *"
                  value={manualEntry.description}
                  onChange={(e) => setManualEntry((prev) => ({ ...prev, description: e.target.value }))}
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <DatePicker
                  label="Date *"
                  value={manualEntry.date}
                  onChange={(date) => setManualEntry((prev) => ({ ...prev, date }))}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={6}>
                <TimePicker
                  label="Start Time *"
                  value={manualEntry.startTime}
                  onChange={(time) => setManualEntry((prev) => ({ ...prev, startTime: time }))}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={6}>
                <TimePicker
                  label="End Time *"
                  value={manualEntry.endTime}
                  onChange={(time) => setManualEntry((prev) => ({ ...prev, endTime: time }))}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Hourly Rate ($)"
                  type="number"
                  value={(manualEntry.hourlyRate / 100).toFixed(2)}
                  onChange={(e) =>
                    setManualEntry((prev) => ({ ...prev, hourlyRate: Math.round(parseFloat(e.target.value) * 100) || 0 }))
                  }
                  InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={manualEntry.billable}
                      onChange={(e) => setManualEntry((prev) => ({ ...prev, billable: e.target.checked }))}
                    />
                  }
                  label="Billable"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setManualEntryOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSaveManualEntry}
              disabled={mutations.createTimeEntry.isPending}
            >
              Save Entry
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation */}
        <ConfirmDialog
          open={!!deleteEntry}
          title="Delete Time Entry"
          message={`Are you sure you want to delete this time entry? This action cannot be undone.`}
          confirmText="Delete"
          variant="danger"
          onConfirm={handleDeleteEntry}
          onCancel={() => setDeleteEntry(null)}
          loading={mutations.deleteTimeEntry.isPending}
        />

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
    </Box>
  );
};

export default TimeTrackingPage;
